import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { useNavigate } from 'react-router';
import LndButton from '@components/LndButton';
import BillingService, { isBillingSessionExpiredError } from '@apis/BillingService';
import { Briefcase, Warning2 } from 'iconsax-react';
import AnimatedArrowRight from '@components/icons/AnimatedArrowRight';
import { useAuth } from '@contexts/AuthContext';
import { UserRole } from '@enums/Permissions';
import posthog from '@libs/posthog';
import { EVENTS } from '@lib/analytics';
import { getPromoterCode } from '@utils/promoterCode';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import type { PlanPrice } from '@customTypes/billing';

interface SubscriptionRequiredModalProps {
  open: boolean;
  onClose: () => void;
}

interface Feature {
  text: string;
  included: boolean;
}

const CheckIcon = () => (
  <svg
    className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-600"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const proFeatures: Feature[] = [
  { text: 'Full funnel audit', included: true },
  { text: 'Unlimited image and video creation', included: true },
  { text: 'Campaign optimizations', included: true },
  { text: 'Automated campaign management', included: true },
];

const SubscriptionRequiredModal: React.FC<SubscriptionRequiredModalProps> = ({
  open,
  onClose,
}) => {
  const { currentUser, logout } = useAuth();
  const canManageBilling = currentUser?.user_type === UserRole.ADMIN;
  const hasSubscription = !!currentUser?.subscription;
  // The free trial is a first-time offer. A returning subscriber (one who already
  // has a subscription record) checks out straight into a paid plan — mirrors the
  // backend, which only grants trial_period_days when there's no prior record.
  const eligibleForTrial = !hasSubscription;
  const navigate = useNavigate();
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<PlanPrice[]>([]);
  const promoterCode = getPromoterCode();
  const screens = useBreakpoint();
  const isMobile = !screens.sm;

  useEffect(() => {
    if (open && canManageBilling) {
      BillingService.getPrices()
        .then(({ plans }) => setPlans(plans))
        .catch(() => {});
    }
  }, [open, canManageBilling]);

  const handleGoToBilling = () => {
    onClose();
    navigate('/settings?tab=billing');
  };

  const handleSelectPlan = async (priceId?: string) => {
    if (!canManageBilling) return;

    setLoadingPlan(priceId || 'default');
    posthog.capture(EVENTS.BILLING.CHECKOUT_INITIATED, {
      source: 'subscription_required_modal',
      price_id: priceId,
    });
    BillingService.createCheckoutSession(priceId).then(({ url }) => {
      if (url) {
        window.location.href = url;
      } else {
        handleGoToBilling();
      }
    }).catch((err) => {
      console.error('Error creating checkout session:', err);
      if (isBillingSessionExpiredError(err)) {
        setError('Session expired. Redirecting to login...');
        window.location.href = '/';
        return;
      }
      setError('Failed to create checkout session.');
    }).finally(() => {
      setLoadingPlan(null);
    });
  };

  const proPlan = plans.find((p) => p.tier === 'business') || {
    tier: 'business', name: 'Pro', amount: 229, price_id: '', currency: 'USD', interval: 'month',
  };

  return (
    <Modal
      open={open}
      onCancel={hasSubscription ? onClose : undefined}
      footer={null}
      closable={false}
      closeIcon={null}
      maskClosable={hasSubscription}
      keyboard={hasSubscription}
      width={isMobile ? '100%' : 460}
      centered
    >
      <div className="flex flex-col">
        <div className="p-6 pb-4 text-center">
          <h2 className="text-2xl font-bold mb-1 text-gray-900">
            {eligibleForTrial ? 'Start Your Free Trial' : 'Subscribe to Leo'}
          </h2>
          {canManageBilling ? (
            <p className="text-gray-500">
              {eligibleForTrial
                ? 'Get full access to Leo — free for 14 days.'
                : 'Get full access to Leo.'}
            </p>
          ) : (
            <p className="text-gray-500">
              The main user of this account needs to subscribe to proceed further.
            </p>
          )}
        </div>

        {canManageBilling ? (
          <div className="border-t border-neutral-100">
            <div className="p-10 pt-6 flex flex-col">
              <div className="flex items-center gap-2 mb-1 text-gray-900">
                <Briefcase size={22} color="currentColor" variant="Outline" />
                <h3 className="text-xl font-medium text-gray-900 m-0">
                  Pro
                </h3>
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                {eligibleForTrial ? (
                  <>
                    <span className="text-xl font-extrabold text-gray-900 line-through decoration-2">
                      ${proPlan.amount}
                    </span>
                    <span className="text-3xl font-extrabold text-gray-900">
                      $0
                    </span>
                    <span className="text-sm text-gray-500">
                      for 14 days
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-3xl font-extrabold text-gray-900">
                      ${proPlan.amount}
                    </span>
                    <span className="text-sm text-gray-500">
                      per month
                    </span>
                  </>
                )}
              </div>
              <p className="mt-3 text-sm text-gray-700 leading-relaxed">
                {eligibleForTrial
                  ? 'Free for 14 days — cancel anytime.'
                  : 'Billed monthly — cancel anytime.'}
              </p>
              <div className="pt-6 mt-4 border-t border-neutral-100">
                <ul className="space-y-3 list-none p-0 m-0">
                  {proFeatures.map((f) => (
                    <li key={f.text} className="flex items-start gap-3 text-sm">
                      <CheckIcon />
                      <span className="text-gray-900">{f.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="pt-6 mt-auto">
                <LndButton
                  variant="primary"
                  className="w-full group"
                  loading={loadingPlan === 'business'}
                  size="large"
                  onClick={() => {
                    setLoadingPlan('business');
                    handleSelectPlan(proPlan.price_id || undefined);
                  }}
                >
                  <span className="inline-flex items-center gap-1">
                    {eligibleForTrial ? 'Start Your Free Trial' : 'Subscribe'}
                    <AnimatedArrowRight />
                  </span>
                </LndButton>
              </div>
            </div>
            {promoterCode && (
              <p className="text-xs text-gray-500 mt-2 opacity-50 text-center pb-4">
                Applying promoter code: <span className="font-medium">{promoterCode}</span>
              </p>
            )}
            {error && (
              <div className="flex items-center justify-center gap-2 pb-4">
                <Warning2 size={16} color="red" />
                <p className="text-red-500">{error}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="px-6 pb-6">
            {hasSubscription && (
              <LndButton
                onClick={onClose}
                variant="link"
                className="flex-1 w-full"
              >
                Close
              </LndButton>
            )}
          </div>
        )}

        <div className="text-center pb-4">
          <button
            type="button"
            onClick={() => logout()}
            className="text-xs text-gray-500 hover:text-gray-700 hover:underline bg-transparent border-0 p-0 cursor-pointer"
          >
            Log out
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SubscriptionRequiredModal;
